import type { BoardEntry, BoardMode } from '../core/types';
import { fetchBoard, type BoardData } from '../net/board';
import { shiftDate, todayUTC } from '../net/dates';
import { AI_ROSTER } from '../game/ai-roster';

interface Tab { id: string; label: string }

const MODE_TABS: Tab[] = [
  { id: 'daily', label: '日课 6′' }, { id: 'race', label: '日赛 14′' },
];
const REGION_TABS: Tab[] = [
  { id: 'global', label: '全球' }, { id: 'asia', label: '亚洲' },
  { id: 'eu', label: '欧洲' }, { id: 'na', label: '北美' },
  { id: 'oceania', label: '大洋洲' }, { id: 'africa', label: '非洲' },
];

export function fmtClock(t: number): string {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** 榜单浮层：模式 / 地区 / 日期切换；名号与 AI 行全部 textContent 防 XSS。 */
export class BoardView {
  private mode: BoardMode = 'daily';
  private region = 'global';
  private date = todayUTC();

  constructor(
    private list: HTMLElement,
    private snap: HTMLElement,
    private dateLabel: HTMLElement,
    modeTabs: HTMLElement,
    regionTabs: HTMLElement,
    prevBtn: HTMLElement,
    nextBtn: HTMLElement,
    private selfToken: () => string,
  ) {
    this.buildTabs(modeTabs, MODE_TABS, () => this.mode, (id) => {
      this.mode = id as BoardMode; this.rebuildTabs(); this.refresh();
    });
    this.buildTabs(regionTabs, REGION_TABS, () => this.region, (id) => {
      this.region = id; this.rebuildTabs(); this.refresh();
    });
    prevBtn.addEventListener('click', () => {
      this.date = shiftDate(this.date, -1); this.refresh();
    });
    nextBtn.addEventListener('click', () => {
      if (this.date < todayUTC()) this.date = shiftDate(this.date, 1);
      this.refresh();
    });
  }

  private tabContainers: HTMLElement[] = [];
  private buildTabs(
    c: HTMLElement, tabs: Tab[], active: () => string,
    onPick: (id: string) => void,
  ): void {
    this.tabContainers.push(c);
    c.innerHTML = '';
    for (const t of tabs) {
      const b = document.createElement('button');
      b.className = 'board-tab' + (active() === t.id ? ' active' : '');
      b.textContent = t.label;
      b.addEventListener('click', () => onPick(t.id));
      c.append(b);
    }
  }

  private rebuildTabs(): void {
    this.buildTabs(this.tabContainers[0], MODE_TABS, () => this.mode,
      (id) => { this.mode = id as BoardMode; this.rebuildTabs(); this.refresh(); });
    this.buildTabs(this.tabContainers[1], REGION_TABS, () => this.region,
      (id) => { this.region = id; this.rebuildTabs(); this.refresh(); });
  }

  open(): void { this.refresh(); }

  private async refresh(): Promise<void> {
    this.dateLabel.textContent = this.date;
    this.list.textContent = '读取中…';
    this.snap.textContent = '';
    let data: BoardData;
    try {
      data = await fetchBoard(this.date, this.mode, this.region);
    } catch {
      this.list.textContent = '榜单读取失败，请稍后再试。';
      return;
    }
    this.render(data);
  }

  private render(data: BoardData): void {
    this.list.innerHTML = '';
    if (data.top.length === 0) {
      this.list.textContent = '当日该榜暂无成绩。';
    }
    for (const e of data.top) {
      this.list.append(this.rowEl(e));
    }
    if (data.generatedAt) {
      const hhmm = data.generatedAt.slice(11, 16) + ' UTC';
      this.snap.textContent =
        `快照 ${hhmm} · 真人 ${data.total} 人 · 位次稍后更新（约每 7 分钟）`;
    } else {
      this.snap.textContent = '暂无快照，提交后约 7 分钟内更新。';
    }
  }

  private rowEl(e: BoardEntry): HTMLElement {
    const div = document.createElement('div');
    const me = e.token === this.selfToken();
    div.className = 'board-row'
      + (e.ai ? ' board-row-ai' : '') + (me ? ' board-row-me' : '');
    const rank = document.createElement('span');
    rank.className = 'board-rank';
    rank.textContent = e.ai ? '◆' : `#${e.rank}`;
    const name = document.createElement('span');
    name.className = 'board-name';
    if (e.ai) {
      const r = AI_ROSTER.find((x) => x.aiId === e.token);
      name.textContent = `[AI] ${r?.callsign ?? e.token} · ${e.ai}`;
    } else {
      name.textContent = e.name?.trim() || e.token.slice(0, 6);
    }
    const meta = document.createElement('span');
    meta.className = 'board-meta';
    meta.textContent = `${e.score} · ${e.kills}杀 · ${fmtClock(e.time)}`;
    div.append(rank, name, meta);
    return div;
  }
}
